import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Building2, Check, Eye, EyeOff, Loader2, MoreVertical, UtensilsCrossed, X } from 'lucide-react'
import { menuService } from '../services/menuService'
import { useSelectedPropertyId, useSelectedPropertyType } from '../store/propertyStore'
import type { MenuItem, MenuItemUpdate } from '../types/api.types'
import { AdminBanner } from '../features/admin/components/AdminBanner'
import { CardOverflowMenu } from '../features/admin/components/CardOverflowMenu'
import { EmptyState } from '../features/admin/components/EmptyState'

const UNCATEGORISED = 'Uncategorised'
const NEW_CATEGORY = '__new__'
const PRICE_PATTERN = /^\d+(\.\d{1,2})?$/
const WHOLE_NUMBER = /^\d+$/
const MAX_INGREDIENTS = 50

interface FormState {
  name: string
  category: string
  price: string
  description: string
  imageUrl: string
  available: boolean
  // Comma-separated in the box; split into an array before sending
  // (contract: ingredients is a JSON array, a delimited string 422s).
  ingredients: string
  calories: string
  waitMinutes: string
}

const EMPTY_FORM: FormState = {
  name: '', category: '', price: '', description: '', imageUrl: '', available: true,
  ingredients: '', calories: '', waitMinutes: '',
}

// [] and 0 mean "not filled in" on the wire, so an empty box round-trips clean.
function formFrom(item: MenuItem): FormState {
  return {
    name: item.name,
    category: item.category ?? '',
    price: item.price ?? '',
    description: item.description ?? '',
    imageUrl: item.image_url ?? '',
    available: item.available,
    ingredients: item.ingredients.join(', '),
    calories: item.calories ? String(item.calories) : '',
    waitMinutes: item.estimated_wait_minutes ? String(item.estimated_wait_minutes) : '',
  }
}

function parseIngredients(text: string): string[] {
  return text.split(',').map(s => s.trim()).filter(Boolean)
}

// Handoff §4.2: absent and null mean different things, so send only what moved.
// A form that PUTs every field would clear whatever the user left blank.
function changedFields(original: MenuItem, form: FormState): MenuItemUpdate {
  const next = {
    name: form.name.trim(),
    category: form.category.trim() || null,
    price: form.price.trim() || null,
    description: form.description.trim() || null,
    image_url: form.imageUrl.trim() || null,
    available: form.available,
    // Never null on the wire — an emptied box clears with [] / 0.
    ingredients: parseIngredients(form.ingredients),
    calories: Number(form.calories.trim() || 0),
    estimated_wait_minutes: Number(form.waitMinutes.trim() || 0),
  }
  const patch: MenuItemUpdate = {}
  if (next.name !== original.name) patch.name = next.name
  if (next.category !== original.category) patch.category = next.category
  if (next.price !== original.price) patch.price = next.price
  if (next.description !== original.description) patch.description = next.description
  if (next.image_url !== original.image_url) patch.image_url = next.image_url
  if (next.available !== original.available) patch.available = next.available
  if (next.ingredients.join('\n') !== original.ingredients.join('\n')) patch.ingredients = next.ingredients
  if (next.calories !== original.calories) patch.calories = next.calories
  if (next.estimated_wait_minutes !== original.estimated_wait_minutes) {
    patch.estimated_wait_minutes = next.estimated_wait_minutes
  }
  return patch
}

// Handoff §6: the order path refuses an available item with no price and the
// customer just hears "we're out of that tonight". Until the backend rule
// lands, this is the only thing standing between a manager and a silent hole.
function isUnorderable(item: { available: boolean; price: string | null }): boolean {
  return item.available && item.price == null
}

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

function statusOf(e: unknown): number | undefined {
  return typeof e === 'object' && e !== null && 'status' in e
    ? (e as { status?: number }).status
    : undefined
}

// Same shape as CardOverflowMenu, but that one's Edit/Delete pair is shared by
// three other tabs — this menu's labels (Rename / Remove category) are specific
// to a category heading, so it stays local rather than genericizing the shared one.
function CategoryMenu({ onRename, onRemove }: { onRename: () => void; onRemove: () => void }): JSX.Element {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        aria-label="Category actions"
      >
        <MoreVertical size={14} />
      </button>
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            onClick={() => { setOpen(false); onRename() }}
            className="flex w-full items-center px-3 py-2 text-left text-[13px] text-gray-700 hover:bg-gray-50"
          >
            Rename
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onRemove() }}
            className="flex w-full items-center px-3 py-2 text-left text-[13px] text-red-600 hover:bg-red-50"
          >
            Remove category
          </button>
        </div>
      )}
    </div>
  )
}

export function MenuPage(): JSX.Element {
  const propertyId = useSelectedPropertyId()
  const propertyType = useSelectedPropertyType()

  const [items, setItems] = useState<MenuItem[]>([])
  const [currency, setCurrency] = useState('PKR')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<number | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<MenuItem | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [newCategory, setNewCategory] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<MenuItem | null>(null)

  const [renamingCategory, setRenamingCategory] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameError, setRenameError] = useState<string | null>(null)
  const [renameSubmitting, setRenameSubmitting] = useState(false)
  const [pendingMerge, setPendingMerge] = useState<{ from: string; to: string } | null>(null)
  const [removingCategory, setRemovingCategory] = useState<string | null>(null)
  const [removeSubmitting, setRemoveSubmitting] = useState(false)

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 5000); return () => clearTimeout(t) } }, [success])
  useEffect(() => { if (error) { const t = setTimeout(() => setError(null), 5000); return () => clearTimeout(t) } }, [error])

  const loadMenu = useCallback(async () => {
    if (propertyId == null) { setItems([]); setLoading(false); return }
    setLoading(true)
    try {
      const menu = await menuService.get(propertyId)
      setItems(menu.items)
      setCurrency(menu.currency || 'PKR')
    } catch (e) {
      setError(errorMessage(e, 'Failed to load the menu'))
    } finally {
      setLoading(false)
    }
  }, [propertyId])

  useEffect(() => { loadMenu() }, [loadMenu])

  const grouped = useMemo(() => {
    const byCategory = new Map<string, MenuItem[]>()
    for (const item of items) {
      const key = item.category?.trim() || UNCATEGORISED
      const bucket = byCategory.get(key)
      if (bucket) bucket.push(item)
      else byCategory.set(key, [item])
    }
    // Uncategorised last, everything else alphabetical.
    return [...byCategory.entries()].sort(([a], [b]) => {
      if (a === UNCATEGORISED) return 1
      if (b === UNCATEGORISED) return -1
      return a.localeCompare(b)
    })
  }, [items])

  // The categories that already exist, for the dropdown. Free text on the API,
  // so this is the only thing keeping them from drifting into near-duplicates.
  const categories = useMemo(() => {
    const found = new Set<string>()
    for (const item of items) {
      const trimmed = item.category?.trim()
      if (trimmed) found.add(trimmed)
    }
    return [...found].sort((a, b) => a.localeCompare(b))
  }, [items])

  const unorderableCount = items.filter(isUnorderable).length

  function openCreate() {
    setEditing(null)
    setForm(EMPTY_FORM)
    setNewCategory(false)
    setFormError(null)
    setNameError(null)
    setShowModal(true)
  }

  function openEdit(item: MenuItem) {
    setEditing(item)
    setForm(formFrom(item))
    setNewCategory(false)
    setFormError(null)
    setNameError(null)
    setShowModal(true)
  }

  function pickCategory(value: string) {
    if (value === NEW_CATEGORY) {
      setNewCategory(true)
      setForm(f => ({ ...f, category: '' }))
    } else {
      setNewCategory(false)
      setForm(f => ({ ...f, category: value }))
    }
  }

  function applySaveError(e: unknown) {
    // Handoff §5: 409 is the failure managers hit most, usually because the
    // item is already there further down a long list. Put it on the field.
    if (statusOf(e) === 409) setNameError(errorMessage(e, 'That name is already on this menu'))
    else setFormError(errorMessage(e, 'Failed to save this item'))
  }

  async function handleSubmit() {
    const name = form.name.trim()
    if (!name) { setNameError('Name is required'); return }
    const price = form.price.trim()
    if (price && !PRICE_PATTERN.test(price)) {
      setFormError('Price must be a number with at most two decimal places, e.g. 350 or 349.50')
      return
    }
    const calories = form.calories.trim()
    if (calories && !WHOLE_NUMBER.test(calories)) {
      setFormError('Calories must be a whole number, e.g. 780')
      return
    }
    const waitMinutes = form.waitMinutes.trim()
    if (waitMinutes && !WHOLE_NUMBER.test(waitMinutes)) {
      setFormError('Wait time must be whole minutes, e.g. 25')
      return
    }
    const ingredients = parseIngredients(form.ingredients)
    if (ingredients.length > MAX_INGREDIENTS) {
      setFormError(`At most ${MAX_INGREDIENTS} ingredients — this has ${ingredients.length}`)
      return
    }

    setSubmitting(true)
    setFormError(null)
    setNameError(null)
    try {
      if (editing) {
        const patch = changedFields(editing, form)
        if (Object.keys(patch).length > 0) await menuService.update(editing.id, patch)
        setSuccess('Item updated')
      } else {
        await menuService.create({
          property_id: propertyId!,
          name,
          category: form.category.trim() || null,
          price: price || null,
          description: form.description.trim() || null,
          image_url: form.imageUrl.trim() || null,
          available: form.available,
          ingredients,
          calories: Number(calories || 0),
          estimated_wait_minutes: Number(waitMinutes || 0),
        })
        setSuccess('Item added')
      }
      setShowModal(false)
      await loadMenu()
    } catch (e) {
      applySaveError(e)
    } finally {
      setSubmitting(false)
    }
  }

  async function toggleAvailable(item: MenuItem) {
    setTogglingId(item.id)
    try {
      await menuService.update(item.id, { available: !item.available })
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, available: !i.available } : i)))
      setSuccess(item.available ? `${item.name} hidden from the menu` : `${item.name} is back on the menu`)
    } catch (e) {
      setError(errorMessage(e, 'Failed to change availability'))
    } finally {
      setTogglingId(null)
    }
  }

  async function confirmDelete() {
    const item = deleteTarget
    if (!item) return
    setDeleteTarget(null)
    try {
      await menuService.delete(item.id)
      setSuccess(`${item.name} deleted`)
      await loadMenu()
    } catch (e) {
      setError(errorMessage(e, 'Failed to delete this item'))
    }
  }

  async function hideInstead() {
    const item = deleteTarget
    if (!item) return
    setDeleteTarget(null)
    await toggleAvailable(item)
  }

  function itemCount(category: string): number {
    return grouped.find(([c]) => c === category)?.[1].length ?? 0
  }

  function startRename(category: string) {
    setRenamingCategory(category)
    setRenameValue(category)
    setRenameError(null)
  }

  function cancelRename() {
    setRenamingCategory(null)
    setRenameValue('')
    setRenameError(null)
  }

  // Handoff: renaming onto a name already in use merges the two groups. Not
  // reversible in one call, so confirm before it fires when that's the case.
  function submitRename() {
    if (!renamingCategory) return
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === renamingCategory) return
    const merging = categories.some(c => c !== renamingCategory && c === trimmed)
    if (merging) setPendingMerge({ from: renamingCategory, to: trimmed })
    else doRename(renamingCategory, trimmed)
  }

  async function doRename(from: string, to: string) {
    setRenameSubmitting(true)
    setRenameError(null)
    try {
      const result = await menuService.renameCategory(propertyId!, from, to)
      setSuccess(`${result.items_updated} ${result.items_updated === 1 ? 'item' : 'items'} moved`)
      setRenamingCategory(null)
      setPendingMerge(null)
      await loadMenu()
    } catch (e) {
      setPendingMerge(null)
      // Handoff: 404 means another manager already renamed/emptied this
      // group — the category list is stale, not "not found". Refetch.
      if (statusOf(e) === 404) {
        setRenamingCategory(null)
        setError('That category changed — refreshed the menu.')
        await loadMenu()
      } else if (statusOf(e) === 403) {
        setError('You need manager access to edit this menu.')
      } else {
        setRenameError(errorMessage(e, 'Failed to rename this category'))
      }
    } finally {
      setRenameSubmitting(false)
    }
  }

  function startRemoveCategory(category: string) {
    setRemovingCategory(category)
  }

  async function confirmRemoveCategory() {
    const category = removingCategory
    if (!category) return
    setRemoveSubmitting(true)
    try {
      const result = await menuService.deleteCategory(propertyId!, category)
      setSuccess(`${result.items_updated} ${result.items_updated === 1 ? 'item' : 'items'} moved to ${UNCATEGORISED}`)
      setRemovingCategory(null)
      await loadMenu()
    } catch (e) {
      setRemovingCategory(null)
      if (statusOf(e) === 404) {
        setError('That category changed — refreshed the menu.')
        await loadMenu()
      } else if (statusOf(e) === 403) {
        setError('You need manager access to edit this menu.')
      } else {
        setError(errorMessage(e, 'Failed to remove this category'))
      }
    } finally {
      setRemoveSubmitting(false)
    }
  }

  if (propertyId == null) {
    return (
      <EmptyState
        icon={Building2}
        message="Select a property"
        hint="Pick one from the bar at the top to see its menu."
      />
    )
  }

  if (propertyType !== 'RESTAURANT') {
    return (
      <EmptyState
        icon={UtensilsCrossed}
        message="Select a restaurant"
        hint="Menus belong to restaurant properties. Switch property from the bar above."
      />
    )
  }

  const savingUnorderable = form.available && !form.price.trim()

  // The modals are siblings of the page stack, not children of it: `space-y-5`
  // sets margin-top on every child after the first, and on a `fixed inset-0`
  // backdrop that margin shifts the overlay down and leaves an undimmed strip
  // across the top of the screen.
  return (
    <>
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-bold text-gray-900">Menu</h1>
          <p className="text-[13px] text-gray-500">
            {items.length} {items.length === 1 ? 'item' : 'items'} · prices in {currency}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="shrink-0 rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black transition-opacity hover:opacity-90"
        >
          Add item
        </button>
      </div>

      <AdminBanner message={error} variant="error" />
      <AdminBanner message={success} variant="success" />

      {unorderableCount > 0 && (
        <p className="flex items-center gap-2 text-[13px] text-amber-700">
          <AlertTriangle size={14} className="shrink-0" />
          {unorderableCount} available {unorderableCount === 1 ? 'item has' : 'items have'} no price
          — the voice agent will refuse {unorderableCount === 1 ? 'it' : 'them'}.
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-brand-green" />
          <span className="ml-2 text-sm text-gray-500">Loading menu...</span>
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={UtensilsCrossed} message="No menu items yet" hint="Add your first dish so the voice agent has something to offer" />
      ) : (
        grouped.map(([category, categoryItems]) => (
          <section key={category}>
            <div className="mb-1.5 flex items-center gap-1.5">
              {renamingCategory === category ? (
                <div className="flex flex-1 flex-wrap items-center gap-1.5">
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') submitRename()
                      if (e.key === 'Escape') cancelRename()
                    }}
                    aria-label={`Rename ${category}`}
                    className="rounded border border-gray-200 px-2 py-1 text-[13px] text-gray-900 outline-none focus:border-brand-green"
                  />
                  <button
                    type="button"
                    onClick={submitRename}
                    disabled={renameSubmitting || !renameValue.trim() || renameValue.trim() === category}
                    aria-label="Save category name"
                    className="rounded p-1 text-brand-green hover:bg-gray-100 disabled:opacity-40"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={cancelRename}
                    aria-label="Cancel rename"
                    className="rounded p-1 text-gray-400 hover:bg-gray-100"
                  >
                    <X size={14} />
                  </button>
                  {renameError && <span className="text-[12px] text-red-600">{renameError}</span>}
                </div>
              ) : (
                <>
                  <h2 className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                    {category} · {categoryItems.length}
                  </h2>
                  {category !== UNCATEGORISED && (
                    <CategoryMenu
                      onRename={() => startRename(category)}
                      onRemove={() => startRemoveCategory(category)}
                    />
                  )}
                </>
              )}
            </div>
            {/* No overflow-hidden: it clipped the last row's actions menu inside the
                card. The rows round their own outer corners instead, so the hover
                fill still stops at the border. */}
            <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
              {categoryItems.map(item => (
                <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 first:rounded-t-lg last:rounded-b-lg hover:bg-gray-50">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`truncate text-[14px] ${item.available ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                        {item.name}
                      </span>
                      {isUnorderable(item) && (
                        <span
                          title="Available with no price — the voice agent refuses it"
                          className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
                        >
                          No price
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="truncate text-[12px] text-gray-400">{item.description}</p>
                    )}
                    {/* 0 means "not filled in yet" (contract) — hide, don't print "0 kcal". */}
                    {(item.calories > 0 || item.estimated_wait_minutes > 0) && (
                      <p className="truncate text-[12px] text-gray-400">
                        {[
                          item.calories > 0 ? `${item.calories} kcal` : null,
                          item.estimated_wait_minutes > 0 ? `~${item.estimated_wait_minutes} min` : null,
                        ].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>

                  <span className="shrink-0 tabular-nums text-[14px] text-gray-600">
                    {item.price ?? '—'}
                  </span>

                  <button
                    type="button"
                    onClick={() => toggleAvailable(item)}
                    disabled={togglingId === item.id}
                    aria-label={item.available ? `Hide ${item.name}` : `Show ${item.name}`}
                    title={item.available ? 'Not serving this tonight' : 'Put back on the menu'}
                    className="shrink-0 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                  >
                    {togglingId === item.id
                      ? <Loader2 size={16} className="animate-spin" />
                      : item.available ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <CardOverflowMenu onEdit={() => openEdit(item)} onDelete={() => setDeleteTarget(item)} />
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="flex max-h-[90dvh] w-full max-w-md flex-col rounded-xl bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-900">{editing ? 'Edit item' : 'Add item'}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-600">{formError}</div>
              )}

              <div>
                <label htmlFor="menu-name" className="mb-1 block text-[13px] font-medium text-gray-700">Name*</label>
                <input
                  id="menu-name"
                  value={form.name}
                  onChange={e => { setForm({ ...form, name: e.target.value }); setNameError(null) }}
                  className={`w-full rounded-lg border px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green ${
                    nameError ? 'border-red-400' : 'border-gray-200'
                  }`}
                  placeholder="Kashmiri Chai"
                />
                {nameError && <p className="mt-1 text-[12px] text-red-600">{nameError}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="menu-category" className="mb-1 block text-[13px] font-medium text-gray-700">Category</label>
                  <select
                    id="menu-category"
                    value={newCategory ? NEW_CATEGORY : form.category}
                    onChange={e => pickCategory(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  >
                    <option value="">{UNCATEGORISED}</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                    <option value={NEW_CATEGORY}>+ New category</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="menu-price" className="mb-1 block text-[13px] font-medium text-gray-700">Price ({currency})</label>
                  <input
                    id="menu-price"
                    inputMode="decimal"
                    value={form.price}
                    onChange={e => setForm({ ...form, price: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                    placeholder="350"
                  />
                </div>
              </div>

              {newCategory && (
                <div>
                  <label htmlFor="menu-new-category" className="mb-1 block text-[13px] font-medium text-gray-700">New category name</label>
                  <input
                    id="menu-new-category"
                    autoFocus
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                    placeholder="Desserts"
                  />
                  <p className="mt-1 text-[12px] text-gray-400">
                    It appears in the list once this item is saved.
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="menu-description" className="mb-1 block text-[13px] font-medium text-gray-700">Description</label>
                <textarea
                  id="menu-description"
                  rows={2}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder="Pink tea with pistachio"
                />
              </div>

              <div>
                <label htmlFor="menu-ingredients" className="mb-1 block text-[13px] font-medium text-gray-700">Ingredients</label>
                <input
                  id="menu-ingredients"
                  value={form.ingredients}
                  onChange={e => setForm({ ...form, ingredients: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder="chicken, tomato, green chilli"
                />
                <p className="mt-1 text-[12px] text-gray-400">Separate with commas.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="menu-calories" className="mb-1 block text-[13px] font-medium text-gray-700">Calories (kcal)</label>
                  <input
                    id="menu-calories"
                    inputMode="numeric"
                    value={form.calories}
                    onChange={e => setForm({ ...form, calories: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                    placeholder="780"
                  />
                </div>

                <div>
                  <label htmlFor="menu-wait" className="mb-1 block text-[13px] font-medium text-gray-700">Wait time (min)</label>
                  <input
                    id="menu-wait"
                    inputMode="numeric"
                    value={form.waitMinutes}
                    onChange={e => setForm({ ...form, waitMinutes: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                    placeholder="25"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="menu-image" className="mb-1 block text-[13px] font-medium text-gray-700">Image URL</label>
                <input
                  id="menu-image"
                  value={form.imageUrl}
                  onChange={e => setForm({ ...form, imageUrl: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder="https://cdn.example.com/chai.jpg"
                />
              </div>

              <label className="flex items-center gap-2 text-[13px] text-gray-700">
                <input
                  type="checkbox"
                  checked={form.available}
                  onChange={e => setForm({ ...form, available: e.target.checked })}
                  className="h-4 w-4 accent-brand-green"
                />
                Available — customers can order it
              </label>

              {savingUnorderable && (
                <div role="alert" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800">
                  <AlertTriangle size={16} className="mt-px shrink-0" />
                  <span>
                    Available with no price. The voice agent will refuse this item and tell the
                    customer it is unavailable. Add a price, or untick Available.
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-3 sm:flex-row sm:gap-3 sm:px-6 sm:py-4">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-6 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6">
            <h2 className="text-lg font-bold text-gray-900">Delete {deleteTarget.name}?</h2>
            <p className="mt-2 text-[13px] text-gray-600">
              Past orders and receipts keep their own copy of the name and price, so revenue
              reports stay correct. What you lose is the link back to this item.
            </p>
            <p className="mt-2 text-[13px] text-gray-600">
              If it is only off tonight, hide it instead — that keeps the item and its history.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
              {deleteTarget.available && (
                <button
                  type="button"
                  onClick={hideInstead}
                  className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90"
                >
                  Hide instead
                </button>
              )}
              <button
                type="button"
                onClick={confirmDelete}
                className="flex-1 rounded-lg bg-red-600 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
              >
                Delete for good
              </button>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-gray-300 px-6 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingMerge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6">
            <h2 className="text-lg font-bold text-gray-900">Merge categories?</h2>
            <p className="mt-2 text-[13px] text-gray-600">
              "{pendingMerge.to}" already exists. This will move all {itemCount(pendingMerge.from)} items from
              "{pendingMerge.from}" into it.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
              <button
                type="button"
                onClick={() => doRename(pendingMerge.from, pendingMerge.to)}
                disabled={renameSubmitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {renameSubmitting ? 'Merging...' : 'Merge'}
              </button>
              <button
                type="button"
                onClick={() => setPendingMerge(null)}
                className="rounded-lg border border-gray-300 px-6 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {removingCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6">
            <h2 className="text-lg font-bold text-gray-900">Remove category?</h2>
            <p className="mt-2 text-[13px] text-gray-600">
              Remove the "{removingCategory}" category? Its {itemCount(removingCategory)} items stay on the menu,
              without a category.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
              <button
                type="button"
                onClick={confirmRemoveCategory}
                disabled={removeSubmitting}
                className="flex-1 rounded-lg bg-red-600 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {removeSubmitting ? 'Removing...' : 'Remove category'}
              </button>
              <button
                type="button"
                onClick={() => setRemovingCategory(null)}
                className="rounded-lg border border-gray-300 px-6 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
